import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@perxis-auto/ui-kit/lib/components/Button';
import { Empty } from '@perxis-auto/ui-kit/lib/components/Empty';
import { Col, Row } from '@perxis-auto/ui-kit/lib/components/Grid';
import {
  ModelCheckbox,
  ModelCheckboxOption,
} from '@perxis-auto/ui-kit/lib/components/ModelCheckbox';
import { Typography } from '@perxis-auto/ui-kit/lib/components/Typography';
import { CheckboxValueType } from 'antd/es/checkbox/Group';

import PAGES from '@/constants/pages';
import formatFilterValue from '@/features/filters/helpers/formatFilterValue';
import getDependantFiltersToReset from '@/features/filters/helpers/getDependantFiltersToReset';
import useFiltersData from '@/features/filters/hooks/useFiltersData';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { rootActions, rootSelectors } from '@/features/store';
import { useInjection } from '@/services/hooks';
import { INavigationService } from '@/services/NavigationService';
import ServiceTypes from '@/services/ServiceTypes';
import debounce from '@/utils/debounce';

import { ModelListProps } from './ModelList.types';

import styles from './ModelList.module.scss';

// Опции для состояния Model Checkbox в момент загрузки фильтров
const FAKE_MODEL_OPTIONS = Array.from(Array(4)).map<ModelCheckboxOption>((_, index) => ({
  model: {
    name: `Model ${index}`,
  },
  value: String(index),
}));

function ModelList({
  buttonType = 'primary',
  titleLevel = 'h2',
  ...props
}: ModelListProps) {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const navigation = useInjection<INavigationService>(ServiceTypes.Navigation);

  const {
    currentData,
    data: filters = currentData,
    isLoading,
    isUninitialized,
  } = useFiltersData();
  const isModelsLoading = isLoading || isUninitialized;

  const showFilters = useAppSelector(rootSelectors.choice.showFiltersSelector);
  const filterKey = useAppSelector(rootSelectors.config.modelsFilterKeySelector);
  const filterGrid = useAppSelector(rootSelectors.config.filterGridSelector);
  const filterChoice = useAppSelector(rootSelectors.choice.filtersChoiceSelector);
  const filterItem = useAppSelector(state =>
    rootSelectors.filters.itemByKeySelector(state, {
      filterKey,
      filtersData: filters?.data,
    }),
  );

  // HACK: Необходимо отменить обработку события на onChange ModelCheckbox.Group
  // при нажатии на карточку
  const isClickedModelCheckbox = useRef(false);

  const handleCheckboxChange = useCallback(
    (checkedValue: CheckboxValueType[]) => {
      if (isClickedModelCheckbox.current) {
        return;
      }

      dispatch(
        rootActions.choice.setFilters({
          [`${filterKey}`]: formatFilterValue(checkedValue),
        }),
      );

      const resetFilters = getDependantFiltersToReset(
        filterKey,
        filterGrid,
        filterChoice,
      );

      if (!resetFilters.length) {
        return;
      }

      // Подготавливаем объект значений фильтров в формате, подходящем для стора.
      const filterValues: { [key: string]: unknown } = {};
      resetFilters.forEach(filterKey => {
        filterValues[filterKey] = null;
      });

      dispatch(rootActions.choice.setFilters(filterValues));
    },
    [dispatch, filterChoice, filterGrid, filterKey],
  );

  const optionsWithHandler = useMemo(() => {
    const options: ModelCheckboxOption[] = (filterItem?.props?.options ||
      []) as ModelCheckboxOption[];
    return options.map<ModelCheckboxOption>(option => {
      return {
        ...option,
        onChange(e) {
          const { value } = e.target;
          handleCheckboxChange([value]);
          navigation.replace(PAGES.Cars);
          isClickedModelCheckbox.current = true;
        },
      };
    });
  }, [filterItem?.props?.options, handleCheckboxChange, navigation]);

  const handleButtonClick = useCallback(() => {
    navigation.replace(PAGES.Cars);
  }, [navigation]);

  if (!filterItem) {
    return null;
  }

  const { props: filterItemProps, value: filterItemValues = [] } = filterItem;

  const filterItemValuesLength = Array.isArray(filterItemValues)
    ? filterItemValues.length
    : 0;

  const isShowAll = !filterItemValuesLength;

  const isEmpty = optionsWithHandler.length === 0 && !isModelsLoading;

  if (isEmpty) {
    return (
      <Empty
        title={t('modelList.missingData.title')}
        description={<>{t('modelList.missingData.tryAgainLater')}</>}
      />
    );
  }

  return (
    <div className={styles.modelList}>
      <Row gutter={[0, 'vertical-yellow']} justify="center">
        <Col span={24} flex="0 0 auto">
          <Typography.Title
            headingLevel={3}
            level={titleLevel}
            color="darkest"
            className={styles.title}
          >
            {t('modelList.title')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <ModelCheckbox.Group
            isLoading={isModelsLoading}
            layout={{
              xs: 'compact',
              sm: 'full',
            }}
            gutter={[
              {
                xs: 0,
                sm: 18,
              },
              {
                xs: 8,
                sm: 16,
              },
            ]}
            xs={24}
            sm={showFilters ? 24 : 12}
            md={showFilters ? 24 : 12}
            lg={12}
            xl={6}
            xxl={6}
            checkboxLayout="checkbox"
            className={`${styles.modelCheckbox} ${
              isLoading ? styles.modelCheckboxLoading : ''
            }`}
            {...filterItemProps}
            {...props}
            value={filterItemValues as ModelCheckboxOption['value'][]}
            onChange={debounce(handleCheckboxChange)}
            options={isLoading ? FAKE_MODEL_OPTIONS : optionsWithHandler}
          />
        </Col>
        <Col span={24} flex="0 0 auto">
          <Button type={buttonType} onClick={handleButtonClick}>
            {!isShowAll
              ? `${t('modelList.show')} (${filterItemValuesLength})`
              : t('modelList.showAll')}
          </Button>
        </Col>
      </Row>
    </div>
  );
}

export default ModelList;
